import axios from "@/lib/axios";

class ExtendedWarrantyService {
  constructor() {
    this.api = axios;
  }

  // Transaction related methods (keeping the old endpoints)
  async getExtendedWarranties(
    query = {
      page: 1,
      size: 10,
      direction: "ASC",
    },
  ) {
    const { data } = await this.api.get("/extended-warranties", {
      params: query,
    });
    return data;
  }

  // Satış iptali her statüden yapılabilir. Para iadesi İÇERMEZ — iade admin
  // panelindeki refund akışının işi.
  async cancelExtendedWarranty(id, deletedReason) {
    const { data } = await this.api.post(`/extended-warranties/${id}/cancel`, {
      deletedReason: deletedReason || null,
    });
    return data;
  }

  async getExtendedWarranty(id) {
    const { data } = await this.api.get(`/extended-warranties/${id}`);
    return data;
  }

  // Package related methods (new endpoints)
  async getAdditionalServicePlans(
    params = {
      page: 1,
      size: 200,
      status: "ACTIVE",
      name: "",
    },
  ) {
    const { data } = await this.api.get("/additional-service-plans", {
      params,
    });
    return data;
  }

  async getAdditionalServicePlan(id) {
    const { data } = await this.api.get(`/additional-service-plans/${id}`);
    return data;
  }

  async createAdditionalServicePlan(planData) {
    const { data } = await this.api.post("/additional-service-plans", planData);
    return data;
  }

  async updateAdditionalServicePlan(id, planData) {
    const { data } = await this.api.put(
      `/additional-service-plans/${id}`,
      planData,
    );
    return data;
  }

  async deleteAdditionalServicePlan(id) {
    const { data } = await this.api.delete(`/additional-service-plans/${id}`);
    return data;
  }

  // Activation code related methods
  async generateActivationCodes(codeCount) {
    const { data } = await this.api.post(
      "/merchant-activation-code/generate",
      null,
      {
        params: {
          codeCount,
        },
      },
    );
    return data;
  }

  async exportActivationCodes() {
    const { data } = await this.api.get("/merchant-activation-code/export", {
      responseType: "blob",
    });
    return data;
  }

  async getSaleReports(params = {}) {
    const { data } = await this.api.post("/report/extended-warranty", params);
    return data;
  }

  async exportSaleReports(format = "excel") {
    const { data } = await this.api.get("/report/extended-warranty/export", {
      responseType: "blob",
    });
    return data;
  }

  // EPS-4974: Satış Ekle akışı. Gateway /seller-screens/* üzerinden bff-service'e gider;
  // satış, giriş yapan merchant user'a (alt bayi) damgalanır.
  // --- Taslak satış (yarım kalmış "Satış Ekle" akışı) ---
  // Taslak extended_warranty'de değil, bff'teki seller_sale_draft tablosunda tutulur;
  // gerekçe: TASLAK-SATIS-TASARIM.md
  async upsertSaleDraft(payload) {
    const { data } = await this.api.put("/seller-screens/drafts", payload);
    return data;
  }

  // params: { page, size, merchantUserId? } — merchantUserId panelin "Bayi" filtresi
  // ve yalnızca ana merchant için anlamlı; alt bayide BE zaten kendi kırılımını
  // uyguluyor. Boş gönderilmemesi önemli: BE'de null = tüm bayiler.
  async getSaleDrafts(params = { page: 1, size: 10 }) {
    // Boş filtreler (bayi/ad/soyad/statü seçilmemiş) hiç gönderilmez.
    const { data } = await this.api.get("/seller-screens/drafts", {
      params: Object.fromEntries(
        Object.entries(params).filter(([, value]) => value != null && value !== ""),
      ),
    });
    return data;
  }

  async getSaleDraft(draftId) {
    const { data } = await this.api.get(`/seller-screens/drafts/${draftId}`);
    return data;
  }

  async deleteSaleDraft(draftId) {
    const { data } = await this.api.delete(`/seller-screens/drafts/${draftId}`);
    return data;
  }

  async getSaleSummary(items) {
    const { data } = await this.api.post("/seller-screens/summary", { items });
    return data;
  }

  /**
   * EPS-4970 revize: Ürün fiyatına ve kategorisine uyan ek hizmet paketleri —
   * embed'deki (Bilkom) ek garanti kartlarıyla aynı uç. Eşleşme yoksa BE 404 döner.
   * Kategori parametreleri opsiyonel; boş olanlar gönderilmez ki BE o seviyeyi
   * atlasın.
   */
  async resolveBaremPlans(params) {
    const query = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null && value !== "",
      ),
    );
    const { data } = await this.api.get(
      "/additional-service-plans/barem/resolve",
      { params: query },
    );
    return data;
  }

  async createSaleForIndividualCustomer(payload) {
    const { data } = await this.api.post(
      "/seller-screens/complete/individual-customer",
      payload,
    );
    return data;
  }

  // EPS-4971 — online ödeme linki gönderimi. Gövde "Satışı Tamamla" ile birebir
  // aynı; satış müşteri ödemeyi tamamlayınca oluşuyor.
  async sendSalePaymentLinkForIndividualCustomer(payload, channel = "SMS") {
    const { data } = await this.api.post(
      "/seller-screens/sale-payment-links/individual-customer",
      payload,
      { params: { channel } },
    );
    return data;
  }

  // EPS-5097 — "Satışı Yapan Personel" önerileri: merchant'ta daha önce girilmiş
  // isimler, en son kullanılan önce (en fazla 20). Ad listesi (string[]) döner.
  async searchSalesPersonnel(query) {
    const { data } = await this.api.get("/seller-screens/sales-personnel", {
      params: { query: query || undefined },
    });
    return data || [];
  }

  // EPS-4976 — egaranti Plus Adaylar. Sayfalama 1-tabanlı. query: ad-soyad,
  // telefon, e-posta, ürün adı ya da seri no; sort: NEWEST | WARRANTY_EXPIRE.
  async getSalesCandidates({ page = 1, size = 10, query, sort } = {}) {
    const { data } = await this.api.get("/seller-screens/candidates", {
      params: { page, size, query: query || undefined, sort: sort || undefined },
    });
    return data;
  }

  // "Öner": ek onay adımı yok, ödeme linki doğrudan müşteriye gider. Kanal
  // gönderilmezse BE telefon varsa SMS, yoksa e-posta seçer.
  async recommendSalesCandidate(individualCustomerProductId, merchantAdditionalServicePlanId, channel) {
    const { data } = await this.api.post(
      `/seller-screens/candidates/${individualCustomerProductId}/recommend`,
      { merchantAdditionalServicePlanId, channel: channel || null },
    );
    return data;
  }

  async uploadSaleInvoice(file) {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await this.api.post("/files", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      params: { path: "sale-invoices" },
    });
    return data;
  }

  async sendSalePaymentLinkForMerchant(payload, channel = "SMS") {
    const { data } = await this.api.post(
      "/seller-screens/sale-payment-links/merchant",
      payload,
      { params: { channel } },
    );
    return data;
  }

  async createSaleForMerchant(payload) {
    const { data } = await this.api.post(
      "/seller-screens/complete/merchant",
      payload,
    );
    return data;
  }
}

export default new ExtendedWarrantyService();
